import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Editor from 'react-simple-code-editor';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
   handleVariableDrop,
   handleDragOver,
   isVariableDrag,
} from '../../services/dragDrop';
import { InfoTooltip } from './infoTooltip';

interface TextInputProps {
   keyRef?: string;
   otherKey?: any;
   title?: string;
   value?: string;
   placeholder?: string;
   isNormal?: boolean;
   type?: string;
   max?: number;
   min?: number;
   step?: number;
   tooltip?: string;
   handleChange?: (e: any, keyRef?: string, otherKey?: any) => void;
   onVariableDrop?: (newValue: string) => void;
}

export function TextInput({
   keyRef,
   otherKey,
   title,
   value,
   placeholder,
   handleChange,
   onVariableDrop,
   isNormal,
   type,
   max,
   min,
   step,
   tooltip,
}: TextInputProps) {
   const [isDragOver, setIsDragOver] = useState(false);
   const [code, setCode] = useState(value || '');
   const wrapperRef = useRef<HTMLDivElement | null>(null);

   const availableVariables = useSelector(
      (state: RootState) => state.variables.variableDetails.map((item) => item.path)
   );

   useEffect(() => {
      setCode(value || '');
   }, [value]);

   // --- HELPERS
   const getInnerElements = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return { textarea: null, pre: null };
      const textarea = wrapper.querySelector('textarea') as HTMLTextAreaElement | null;
      const pre = wrapper.querySelector('pre') as HTMLPreElement | null;
      return { textarea, pre };
   };

   // --- SCROLL SYNC ---
   const syncScroll = useCallback(() => {
      const { textarea, pre } = getInnerElements();
      if (textarea && pre) pre.scrollLeft = textarea.scrollLeft;
   }, []);

   useEffect(() => {
      const { textarea } = getInnerElements();
      if (!textarea) return;
      textarea.addEventListener('scroll', syncScroll);
      return () => textarea.removeEventListener('scroll', syncScroll);
   }, [syncScroll]);

   // --- BLOCK ENTER, SYNC SCROLL ON ARROWS ---
   const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || (e.shiftKey && e.key === 'Enter')) {
         e.preventDefault();
         return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
         setTimeout(syncScroll, 0);
      }
   }, [syncScroll]);

   // --- CHANGE HANDLER (kept identical) ---
   const handleCodeChange = (newCode: string) => {
      // Remove newline characters
      let processedValue = newCode.replace(/[\r\n]/g, '');

      if (type === 'number') {
         const isVariablePath = availableVariables.includes(newCode.trim());
         if (isVariablePath) {
            setCode(processedValue);
            if (onVariableDrop) onVariableDrop(processedValue);
            else if (handleChange) {
               const syntheticEvent = {
                  target: { value: processedValue, type: type || 'text' },
                  currentTarget: { value: processedValue, type: type || 'text' },
               };
               handleChange(syntheticEvent, keyRef, otherKey);
            }
            return;
         }

         const numericValue = processedValue.replace(/[^0-9.-]/g, '');
         let cleanedValue = numericValue;
         if ((cleanedValue.match(/\./g) || []).length > 1) cleanedValue = cleanedValue.replace(/\.+$/, '');
         if ((cleanedValue.match(/-/g) || []).length > 1) cleanedValue = cleanedValue.replace(/-/g, '');
         if (cleanedValue.startsWith('-')) cleanedValue = '-' + cleanedValue.replace(/-/g, '');
         else cleanedValue = cleanedValue.replace(/-/g, '');
         processedValue = cleanedValue;

         if (processedValue !== '' && processedValue !== '-' && !isNaN(Number(processedValue))) {
            const numValue = Number(processedValue);
            if (min !== undefined && numValue < min) processedValue = min.toString();
            else if (max !== undefined && numValue > max) processedValue = max.toString();

            if (step !== undefined) {
               if (step % 1 === 0) processedValue = Math.round(numValue).toString();
               else {
                  const precision = step.toString().split('.')[1]?.length || 0;
                  processedValue = numValue.toFixed(precision);
               }
            }
         }
      }

      setCode(processedValue);

      if (onVariableDrop) onVariableDrop(processedValue);
      else if (handleChange) {
         const syntheticEvent = {
            target: { value: processedValue, type: type || 'text' },
            currentTarget: { value: processedValue, type: type || 'text' },
         };
         handleChange(syntheticEvent, keyRef, otherKey);
      }
   };

   // --- DRAG/DROP LOGIC (unchanged) ---
   const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      handleVariableDrop(e, code, (newValue) => handleCodeChange(newValue));
   };

   const handleDragEnterCustom = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      if (isVariableDrag(e)) {
         e.preventDefault();
         setIsDragOver(true);
      }
   }, []);

   const handleDragLeaveCustom = useCallback(() => setIsDragOver(false), []);

   const handleDragOverCustom = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      if (!isVariableDrag(e)) return;
      e.preventDefault();

      const textarea = e.currentTarget.querySelector("textarea") as HTMLTextAreaElement;
      if (!textarea) return;

      // Use the actual plain text value from the textarea
      const text = textarea.value;
      const style = window.getComputedStyle(textarea);
      const font = `${style.fontSize} ${style.fontFamily}`;
      const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.font = font;

      // Get the cursor position relative to the textarea
      const rect = textarea.getBoundingClientRect();
      const x = e.clientX - rect.left + textarea.scrollLeft;
      const y = e.clientY - rect.top + textarea.scrollTop;

      const availableWidth = textarea.clientWidth - parseFloat(style.paddingLeft || "0") - parseFloat(style.paddingRight || "0");

      // --- Step 1: split text into "visual" lines (respecting wrap width)
      const physicalLines: { text: string; start: number }[] = [];
      let currentLine = "";
      let currentWidth = 0;
      let globalIndex = 0;

      const pushLine = () => {
         physicalLines.push({ text: currentLine, start: globalIndex - currentLine.length });
         currentLine = "";
         currentWidth = 0;
      };

      // Pre-calculate variable positions for accurate measurement
      const variableRegex = /\{\{\s*[^}]+\s*\}\}/g;
      let variableMatches: { start: number; end: number; text: string }[] = [];
      let match;
      while ((match = variableRegex.exec(text)) !== null) {
         variableMatches.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0]
         });
      }

      for (let i = 0; i < text.length; i++) {
         const ch = text[i];

         // Check if we're at the start of a variable
         const currentVariable = variableMatches.find(v => v.start === i);
         if (currentVariable) {
            // Measure the entire variable as a single unit
            const variableWidth = ctx.measureText(currentVariable.text).width;

            if (currentWidth + variableWidth > availableWidth && currentLine.length > 0) {
               pushLine();
            }

            currentLine += currentVariable.text;
            currentWidth += variableWidth;
            i = currentVariable.end - 1; // Skip ahead to the end of the variable
            globalIndex += currentVariable.text.length;
            continue;
         }

         if (ch === "\n") {
            pushLine();
            globalIndex++;
            continue;
         }

         const w = ctx.measureText(ch).width;
         if (currentWidth + w > availableWidth && currentLine.length > 0) {
            pushLine();
         }

         currentLine += ch;
         currentWidth += w;
         globalIndex++;
      }

      if (currentLine.length > 0) {
         pushLine();
      }

      if (text.endsWith("\n")) {
         physicalLines.push({ text: "", start: text.length });
      }

      // --- Step 2: determine which "visual line" mouse is on
      const lineIndex = Math.max(0, Math.min(Math.floor(y / lineHeight), physicalLines.length - 1));
      const targetLine = physicalLines[lineIndex];
      if (!targetLine) return;

      // --- Step 3: determine horizontal character position within line
      let widthAccum = 0;
      let charOffsetInLine = targetLine.text.length;

      // Re-measure the target line accounting for variables
      let measuredIndex = 0;
      for (let j = 0; j < targetLine.text.length; j++) {
         const remainingText = targetLine.text.substring(j);
         const variableMatch = variableMatches.find(v =>
            v.start >= targetLine.start + j &&
            v.start < targetLine.start + j + v.text.length
         );

         let chWidth: number;
         let charsToSkip = 1;

         if (variableMatch && targetLine.text.startsWith(variableMatch.text, j)) {
            // It's a variable - measure it as a whole
            chWidth = ctx.measureText(variableMatch.text).width;
            charsToSkip = variableMatch.text.length;
         } else {
            // Regular character
            chWidth = ctx.measureText(targetLine.text[j]).width;
         }

         if (widthAccum + chWidth / 2 >= x) {
            charOffsetInLine = j;
            break;
         }

         widthAccum += chWidth;
         j += charsToSkip - 1; // Skip the rest of the variable
      }

      const absoluteCaretIndex = targetLine.start + charOffsetInLine;

      textarea.focus();
      textarea.setSelectionRange(absoluteCaretIndex, absoluteCaretIndex);
   }, [syncScroll]);

   const highlightVariables = (text: string, availableVariables: string[] = []): string => {
      const textStr = text == null ? '' : String(text);
      const formattedText = textStr.replace(/&/g, '&amp;')
         .replace(/</g, '&lt;')
         .replace(/>/g, '&gt;');
      if (!availableVariables.length) return formattedText;

      const variableRegex = /\{\{\s*[^}]+\s*\}\}/g;

      return formattedText.replace(variableRegex, (match) => {
         const cleanMatch = match.replace(/\s+/g, ' ').trim();

         if (availableVariables.includes(cleanMatch)) {
            return `<span class="token variable-highlight${match.includes('$block') ? '-block' : match.includes('$global') ? '-global' : '-workflow'}">${match}</span>`;
         }

         return match;
      });
   };

   return (
      <Main className={`textInput ${keyRef || ''}`}>
         {title ? <h4 style={{ fontSize: isNormal ? '18px' : '15px' }}>{title}<InfoTooltip text={tooltip} /></h4> : null}

         <EditorWrapper ref={wrapperRef} onDrop={handleDrop}>
            <StyledEditor
               $isDragOver={isDragOver}
               $type={type}
               value={code}
               onValueChange={handleCodeChange}
               highlight={(text) => highlightVariables(text, availableVariables)}
               padding={10}
               placeholder={placeholder}
               tabSize={4}
               insertSpaces
               onDragOver={handleDragOverCustom}
               onDragEnter={handleDragEnterCustom}
               onDragLeave={handleDragLeaveCustom}
               onKeyDown={handleKeyDown}
               style={{
                  background: 'transparent',
                  fontFamily: 'monospace',
                  fontSize: 14,
                  lineHeight: '1.5',
               }}
            />
         </EditorWrapper>
      </Main>
   );
}

// --- STYLES ---
const Main = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  position: relative;

  h4 {
    font-weight: 400;
    width: 90px;
    white-space: nowrap;
  }
`;

// OUTER WRAPPER: only element allowed to scroll
const EditorWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 40px;
  overflow-x: auto;
  overflow-y: hidden;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  box-sizing: border-box;
  background-color: #f9fafb;

  /* hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// INNER EDITOR: sync layers + no wrapping
const StyledEditor = styled(Editor) <{ $isDragOver: boolean; $type?: string }>`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  border: 1px solid ${({ $isDragOver }) => ($isDragOver ? '#3b82f6' : 'transparent')};
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  overflow: hidden;

  ${({ $type }) => $type === 'number' && `
    inputmode: numeric;
    pattern: [0-9]*;
  `}

  & > div {
    overflow: hidden !important;
    position: relative;
    height: 100%;
    width: 100%;
  }

  textarea {
    background: transparent !important;
    color: #111827 !important;
    caret-color: #000 !important;
    font-size: 14px;
    font-family: monospace;
    line-height: 1.5;
    white-space: pre !important;
    word-wrap: normal !important;
    word-break: keep-all !important;
    overflow: hidden !important;
    width: 100% !important;
    height: 100% !important;
    resize: none !important;
    outline: none !important;
    border: none !important;
  }

  pre {
    margin: 0;
    white-space: pre !important;
    word-wrap: normal !important;
    word-break: keep-all !important;
    overflow: hidden !important;
    font-size: 14px;
    font-family: monospace;
    line-height: 1.5;
    color: #111827 !important;
    width: 100%;
    height: 100%;
  }

  
.variable-highlight-block {
      color: #10b981;
      font-family: monospace;
      line-height: 1.5;
      background-color: rgba(16, 185, 129, 0.09);
   }
   
   .variable-highlight-global {
      color: #b91010ff;
      font-family: monospace;
      line-height: 1.5;
      background-color: rgba(185, 16, 16, 0.06); // new
   }
   
   .variable-highlight-workflow {
      color: #11a5c0;
      font-family: monospace;
      line-height: 1.5;
      background-color: rgba(17, 165, 192, 0.09);
   }
`;